import {
  type __InputStylesNames,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  type TextInputProps,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import type { ReactElement } from 'react'
import { cloneElement, useCallback, useMemo, useState } from 'react'

import { AISplitButton } from '@/components/AISplitButton'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import * as classes from '@/styles/AIInputWrapper.css'

type AIFieldState = {
  state: 'idle' | 'loading' | 'pending'
  suggestion: string | null
}

type AIInputWrapperProps = {
  fieldState: AIFieldState
  children: ReactElement<TextInputProps>
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  /** When provided, "Guide…" appears in idle dropdown; user can add instructions sent as user_instructions to the generate endpoint. */
  onAskSidekickWithGuide?: (instructions: string) => void
  disabled?: boolean
}

function useStatusDisplay(_fieldState: AIFieldState) {
  return useMemo(
    () => ({
      statusText: '',
    }),
    [],
  )
}

function useInputModification(originalProps: TextInputProps, fieldState: AIFieldState, onReject: () => void) {
  return useMemo(() => {
    const { state, suggestion } = fieldState
    const isPending = state === 'pending'
    const isLoading = state === 'loading'
    const isAIActive = isPending || isLoading
    const classNames = originalProps.classNames as Partial<Record<__InputStylesNames, string>>

    return {
      ...originalProps,
      // Auto-reject suggestion when user starts typing
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isPending) {
          onReject()
        }
        originalProps.onChange?.(e)
      },
      value: isPending ? (suggestion ?? '') : (originalProps.value ?? ''),
      withAsterisk: false,
      variant: isAIActive ? 'filled' : originalProps.variant,
      classNames: {
        ...originalProps.classNames,
        input: theme.cx(classNames?.input, isAIActive && classes.input),
      },
      error: originalProps.error,
    }
  }, [originalProps, fieldState, onReject])
}

// Guide modal content: instructions textarea + Generate / Cancel (exported for reuse in FAQEntry)
export function GuideModalContent({
  instructions,
  onInstructionsChange,
  onGenerate,
  onClose,
  disabled,
}: {
  instructions: string
  onInstructionsChange: (value: string) => void
  onGenerate: () => void
  onClose: () => void
  disabled?: boolean
}) {
  const handleGenerate = useCallback(() => {
    onGenerate()
    onClose()
  }, [onGenerate, onClose])

  return (
    <Stack gap="md">
      <Textarea
        label="Instructions for Sidekick"
        placeholder="e.g. Use a professional tone"
        value={instructions}
        onChange={e => onInstructionsChange(e.target.value)}
        autosize
        minRows={3}
        maxRows={6}
        disabled={disabled}
      />
      <Group gap="sm" justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleGenerate} disabled={disabled}>
          Generate
        </Button>
      </Group>
    </Stack>
  )
}

// Action buttons component
function ActionButtons({
  fieldState,
  onAskSidekick,
  onAccept,
  onReject,
  onRegenerate,
  onAskSidekickWithGuide,
  disabled,
  statusText,
}: {
  fieldState: AIFieldState
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  onAskSidekickWithGuide?: (instructions: string) => void
  disabled?: boolean
  statusText: string
}) {
  const [guideOpen, setGuideOpen] = useState(false)
  const [guideInstructions, setGuideInstructions] = useState('')
  const isNarrowViewport = !useMediaQuery(`(min-width: ${theme.breakpoints.sm})`)

  const handleGuideGenerate = useCallback(() => {
    if (onAskSidekickWithGuide) {
      onAskSidekickWithGuide(guideInstructions.trim())
      setGuideInstructions('')
    }
  }, [onAskSidekickWithGuide, guideInstructions])

  const handleGuideClose = useCallback(() => {
    setGuideOpen(false)
    setGuideInstructions('')
  }, [])

  const buttons = (
    <AISplitButton
      fieldState={fieldState.state}
      onAskSidekick={onAskSidekick}
      onAccept={onAccept}
      onReject={onReject}
      onRegenerate={onRegenerate}
      onGuideClick={onAskSidekickWithGuide ? () => setGuideOpen(true) : undefined}
      disabled={disabled}
    />
  )

  if (!onAskSidekickWithGuide) {
    return (
      <Group align="center" gap="xs">
        {statusText && (
          <Text fw={600} tt="uppercase" size="sm" variant="gradient">
            {statusText}
          </Text>
        )}
        {buttons}
      </Group>
    )
  }

  return (
    <Group align="center" gap="xs">
      {statusText && (
        <Text fw={600} tt="uppercase" size="sm" variant="gradient">
          {statusText}
        </Text>
      )}
      {buttons}
      <Modal
        title="Guide Sidekick"
        opened={guideOpen}
        onClose={handleGuideClose}
        centered
        size="sm"
        fullScreen={isNarrowViewport}>
        <GuideModalContent
          instructions={guideInstructions}
          onInstructionsChange={setGuideInstructions}
          onGenerate={handleGuideGenerate}
          onClose={handleGuideClose}
          disabled={disabled}
        />
      </Modal>
    </Group>
  )
}

export function AIInputWrapper(props: AIInputWrapperProps) {
  const { fieldState, onAskSidekick, onAccept, onReject, onRegenerate, onAskSidekickWithGuide, disabled, children } =
    props

  // Early return if disabled - no modifications needed
  if (disabled) return children

  const statusDisplay = useStatusDisplay(fieldState)
  const modifiedInputProps = useInputModification(children.props, fieldState, onReject)

  const enhancedProps: TextInputProps = {
    ...modifiedInputProps,
    label: (
      <Group justify="space-between" align="center">
        <InputLabel label={children.props.label} withAsterisk={children.props.withAsterisk} withSpacer={false} />

        <ActionButtons
          fieldState={fieldState}
          onAskSidekick={onAskSidekick}
          onAccept={onAccept}
          onReject={onReject}
          onRegenerate={onRegenerate}
          onAskSidekickWithGuide={onAskSidekickWithGuide}
          disabled={disabled}
          statusText={statusDisplay.statusText}
        />
      </Group>
    ),
  }

  return cloneElement(children, enhancedProps)
}
